/**
 * AGROSHOP MALI — Services API React
 * Toutes les fonctions d'appel à l'API Django
 */

// SECURITY NOTE: storing JWT tokens in localStorage is vulnerable to XSS.
// In production prefer HttpOnly Secure cookies for the refresh token
// and keep access tokens in memory only. See backend/SECURITY.md for details.

const API_BASE = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:8000/api' : 'https://api.agroshop.ml/api');

// ============================================================
// CLIENT HTTP — Gestion automatique du token JWT
// ============================================================

class ApiClient {
  constructor() {
    this.accessToken = null; // token kept en mémoire (préféré)
  }

  async init() {
    // Toujours tenter une récupération silencieuse via le cookie HttpOnly
    if (!this.accessToken) {
      return this.refreshToken();
    }
    return true;
  }

  async request(endpoint, options = {}) {
    const token = this.accessToken;
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      credentials: 'include',
      ...options,
    };

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, config);
      
      // Token expiré → rafraîchir automatiquement
      if (response.status === 401) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // Relancer la requête avec le nouveau token en mémoire
          if (this.accessToken) config.headers.Authorization = `Bearer ${this.accessToken}`;
          const retry = await fetch(`${API_BASE}${endpoint}`, config);
          return this.handleResponse(retry);
        } else {
          // Déconnecter l'utilisateur
          this.logout();
          throw new Error('Session expirée. Reconnectez-vous.');
        }
      }

      return this.handleResponse(response);
    } catch (error) {
      if (error.name === 'TypeError') {
        throw new Error('Impossible de se connecter au serveur. Vérifiez votre connexion.');
      }
      throw error;
    }
  }

  async handleResponse(response) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      // Créer un objet d'erreur avec status et data
      const error = new Error();
      // Extraire le message du backend
      const message = data.erreur || data.detail || data.message || 
                      Object.values(data).flat().join(', ') || 
                      `Erreur ${response.status}`;
      error.message = message;
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  }

  async refreshToken() {
    try {
      // Le refresh token est envoyé automatiquement en cookie HttpOnly
      const response = await fetch(`${API_BASE}/auth/refresh/`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        this.accessToken = data.access;
        return true;
      }
    } catch {}
    return false;
  }

  async logout() {
    try {
      await fetch(`${API_BASE}/auth/logout/`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      // Ignorer les erreurs de déconnexion côté réseau
    }
    this.accessToken = null;
  }

  get(endpoint, params = {}) {
    const query = new URLSearchParams(params).toString();
    const url = query ? `${endpoint}?${query}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  post(endpoint, body) {
    return this.request(endpoint, { method: 'POST', body: JSON.stringify(body) });
  }

  patch(endpoint, body) {
    return this.request(endpoint, { method: 'PATCH', body: JSON.stringify(body) });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient();


// ============================================================
// AUTH
// ============================================================

export const authService = {
  async login(telephone, motDePasse) {
    const data = await api.post('/auth/login/', {
      username: telephone.trim(),
      password: motDePasse,
    });
    // Le backend pose le refresh token en cookie HttpOnly; on stocke seulement l'access en mémoire persistante
    api.accessToken = data.access;
    return data;
  },

  async inscription(infos) {
    const data = await api.post('/auth/inscription/', infos);
    api.accessToken = data.access;
    return data;
  },

  logout() {
    api.logout();
  },

  estConnecte() {
    return !!api.accessToken;
  },

  async me() {
    return api.get('/auth/me/');
  },
};


// ============================================================
// CATALOGUE PRODUITS
// ============================================================

export const produitsService = {
  // Liste avec filtres
  getAll(filtres = {}) {
    return api.get('/produits/', filtres);
  },

  // Produits en vedette pour la page d'accueil
  getVedette() {
    return api.get('/produits/vedette/');
  },

  getOffresDuJour() {
    return api.get('/produits/offres_du_jour/');
  },

  getPacksConseils() {
    return api.get('/produits/packs_conseils/');
  },

  // Fiche produit complète
  getDetail(id) {
    return api.get(`/produits/${id}/`);
  },

  getPopulaires() {
    return api.get('/produits/populaires/');
  },

  getComplementaires(produitId) {
    return api.get(`/produits/${produitId}/complementaires/`);
  },

  getReviews(produitId) {
    return api.get(`/produits/${produitId}/avis/`);
  },

  getAchetesEnsemble(produitId) {
    return api.get(`/produits/${produitId}/achetes_ensemble/`);
  },

  // Toutes les catégories
  getCategories() {
    return api.get('/categories/').then((data) => {
      if (Array.isArray(data)) return data;
      return data?.results || [];
    });
  },

  // Recherche produits
  rechercher(terme, filtres = {}) {
    return api.get('/produits/', { search: terme, ...filtres });
  },

  // Par catégorie
  parCategorie(categorieId) {
    return api.get('/produits/', { categorie: categorieId });
  },

  // Stock faible (admin)
  getStockFaible() {
    return api.get('/produits/stock_faible/');
  },

  // Ajuster le stock (admin)
  ajusterStock(produitId, quantite, motif, type = 'ajustement') {
    return api.post(`/produits/${produitId}/ajuster_stock/`, { quantite, motif, type });
  },

  // Créer un produit (admin)
  create(produitData) {
    return api.post('/produits/', produitData);
  },

  // Mettre à jour un produit existant (admin / vendeur)
  update(produitId, updates) {
    return api.patch(`/produits/${produitId}/`, updates);
  },

  // Supprimer un produit (admin / vendeur)
  delete(produitId) {
    return api.delete(`/produits/${produitId}/`);
  },

  // Catalogue vendeur (filtré automatiquement côté backend)
  getVendorProducts() {
    return api.get('/produits/');
  },
};

export const supportService = {
  sendSupportRequest(payload) {
    return api.post('/support/contact/', payload);
  },
};

export const offresService = {
  getAll() {
    return api.get('/offres/');
  },
  create(offreData) {
    return api.post('/offres/', offreData);
  },
  update(offreId, updates) {
    return api.patch(`/offres/${offreId}/`, updates);
  },
  delete(offreId) {
    return api.delete(`/offres/${offreId}/`);
  },
};


// ============================================================
// PANIER (géré localement avec localStorage)
// ============================================================

export const panierService = {
  CLE: 'agroshop_panier',
  RETENTION_MS: 30 * 24 * 60 * 60 * 1000, // 30 jours

  _readCart() {
    const raw = localStorage.getItem(this.CLE);
    if (!raw) return { items: [], updatedAt: null };
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return { items: parsed, updatedAt: null };
      }
      return {
        items: Array.isArray(parsed.items) ? parsed.items : [],
        updatedAt: parsed.updatedAt || null,
      };
    } catch {
      return { items: [], updatedAt: null };
    }
  },

  _writeCart(items, updatedAt = new Date().toISOString()) {
    localStorage.setItem(this.CLE, JSON.stringify({ items, updatedAt }));
    window.dispatchEvent(new Event('panier-mis-a-jour'));
  },

  getItems() {
    const { items, updatedAt } = this._readCart();

    if (updatedAt && Date.now() - new Date(updatedAt).getTime() > this.RETENTION_MS) {
      this.vider();
      return [];
    }

    return items;
  },

  sauvegarder(items) {
    this._writeCart(items);
  },

  ajouter(produit, quantite = 1) {
    const items = this.getItems();
    const index = items.findIndex(i => i.produit.id === produit.id);
    
    if (index >= 0) {
      items[index].quantite += quantite;
    } else {
      items.push({ produit, quantite });
    }
    this.sauvegarder(items);
    return items;
  },

  modifier(produitId, nouvelleQuantite) {
    let items = this.getItems();
    if (nouvelleQuantite <= 0) {
      items = items.filter(i => i.produit.id !== produitId);
    } else {
      const index = items.findIndex(i => i.produit.id === produitId);
      if (index >= 0) items[index].quantite = nouvelleQuantite;
    }
    this.sauvegarder(items);
    return items;
  },

  supprimer(produitId) {
    const items = this.getItems().filter(i => i.produit.id !== produitId);
    this.sauvegarder(items);
    return items;
  },

  vider() {
    this.sauvegarder([]);
  },

  getNbItems() {
    return this.getItems().reduce((total, item) => total + item.quantite, 0);
  },

  getSousTotal() {
    return this.getItems().reduce(
      (total, item) => total + item.produit.prix_unitaire * item.quantite, 0
    );
  },

  mergeItems(localItems, serverItems) {
    const merged = localItems.map((item) => ({ ...item }));
    serverItems.forEach((serverItem) => {
      const index = merged.findIndex(i => i.produit.id === serverItem.produit.id);
      if (index >= 0) {
        merged[index].quantite = Math.min(999, merged[index].quantite + serverItem.quantite);
        merged[index].notes = merged[index].notes || serverItem.notes;
        merged[index].produit = { ...merged[index].produit, ...serverItem.produit };
      } else {
        merged.push(serverItem);
      }
    });
    return merged;
  },

  async saveToServer(extra = {}) {
    if (!api.accessToken) {
      const refreshed = await api.init();
      if (!refreshed) {
        return false;
      }
    }

    const items = this.getItems();
    if (!items.length) {
      return false;
    }

    await commandesService.sauvegarderPanier({
      lignes: items.map((item) => ({
        produit_id: item.produit.id,
        quantite: item.quantite,
        notes: item.notes || '',
      })),
      mode_retrait: extra.mode_retrait || 'livraison',
      ...extra,
    });

    return true;
  },

  async syncFromServer() {
    try {
      if (!api.accessToken) {
        const refreshed = await api.init();
        if (!refreshed) {
          return false;
        }
      }
      const data = await api.get('/commandes/panier/');
      const serverLignes = Array.isArray(data.lignes) ? data.lignes : (data.panier?.lignes || []);
      const localItems = this.getItems();

      if (!serverLignes.length && !localItems.length) {
        return false;
      }

      const serverItems = serverLignes.map((ligne) => ({
        produit: {
          id: ligne.produit,
          nom: ligne.produit_nom,
          prix_unitaire: ligne.prix_unitaire,
          photo_principale: ligne.produit_photo,
          unite: ligne.produit_unite,
          reference: ligne.produit_reference,
        },
        quantite: ligne.quantite,
        notes: ligne.notes || '',
      }));

      if (serverItems.length > 0) {
        const merged = this.mergeItems(localItems, serverItems);
        this.sauvegarder(merged);
        if (localItems.length > 0) {
          await this.saveToServer();
        }
        return true;
      }

      if (localItems.length > 0) {
        await this.saveToServer();
        return true;
      }

      return false;
    } catch (e) {
      return false;
    }
  },
};


export const wishlistService = {
  CLE: 'agroshop_wishlist',

  _readWishlist() {
    const raw = localStorage.getItem(this.CLE);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },

  _writeWishlist(items) {
    localStorage.setItem(this.CLE, JSON.stringify(items));
    window.dispatchEvent(new Event('wishlist-mis-a-jour'));
  },

  getItems() {
    return this._readWishlist();
  },

  isSaved(produitId) {
    return this.getItems().some((item) => item.id === produitId);
  },

  add(produit) {
    const items = this.getItems();
    if (!items.some((item) => item.id === produit.id)) {
      items.push({
        id: produit.id,
        nom: produit.nom,
        reference: produit.reference,
        photo_principale: produit.photo_principale,
        prix_unitaire: produit.prix_unitaire,
        prix_gros: produit.prix_gros,
        quantite_min_gros: produit.quantite_min_gros,
        stock_disponible: produit.stock_disponible,
      });
      this._writeWishlist(items);
    }
    return items;
  },

  remove(produitId) {
    const items = this.getItems().filter((item) => item.id !== produitId);
    this._writeWishlist(items);
    return items;
  },

  toggle(produit) {
    if (this.isSaved(produit.id)) {
      this.remove(produit.id);
      return false;
    }
    this.add(produit);
    return true;
  },
};


export const autoReorderService = {
  CLE: 'agroshop_autoreorder',

  _read() {
    const raw = localStorage.getItem(this.CLE);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },

  _write(items) {
    localStorage.setItem(this.CLE, JSON.stringify(items));
  },

  getAll() {
    return this._read();
  },

  save(sub) {
    const items = this._read();
    items.push(sub);
    this._write(items);
    return items;
  },

  removeByCommandeId(commandeId) {
    const items = this._read().filter((s) => s.commandeId !== commandeId);
    this._write(items);
    return items;
  },
};

// ============================================================
// COMMANDES
// ============================================================

export const commandesService = {
  // Créer une commande depuis le panier
  async creer(donneesCommande) {
    // Convertir les items du panier en format API
    const lignes = panierService.getItems().map(item => ({
      produit_id: item.produit.id,
      quantite: item.quantite,
      notes: item.notes || '',
    }));

    const commande = await api.post('/commandes/', {
      lignes,
      ...donneesCommande,
    });

    // Vider le panier après commande confirmée
    panierService.vider();
    return commande;
  },

  getPanier() {
    return api.get('/commandes/panier/');
  },

  sauvegarderPanier(panierData) {
    return api.post('/commandes/sauvegarder_panier/', panierData);
  },

  checkout(commandeId, data) {
    return api.post(`/commandes/${commandeId}/checkout/`, data);
  },

  reorder(commandeId) {
    return api.post(`/commandes/${commandeId}/reorder/`);
  },

  // Historique des commandes de l'utilisateur connecté
  getHistorique() {
    return api.get('/commandes/');
  },

  // Détail d'une commande
  getDetail(id) {
    return api.get(`/commandes/${id}/`);
  },

  // Suivre une commande par son numéro public
  getByNumero(numero) {
    return api.get('/commandes/numero/', { numero });
  },

  // Annuler une commande confirmée (client propriétaire)
  annuler(id) {
    return api.post(`/commandes/${id}/annuler/`);
  },

  // Changer le statut (admin)
  changerStatut(id, statut, notes = '') {
    return api.post(`/commandes/${id}/changer_statut/`, { statut, notes });
  },
};


// ============================================================
// PAIEMENTS
// ============================================================

export const paiementsService = {
  // Initier un paiement Mobile Money
  async initier(commandeId, operateur, telephone) {
    return api.post('/paiements/initier/', {
      commande_id: commandeId,
      operateur,
      telephone,
    });
  },

  // Vérifier le statut d'un paiement
  verifier(paiementId) {
    return api.get(`/paiements/verifier/${paiementId}/`);
  },

  // Polling — vérifie toutes les 5 secondes pendant 3 minutes max
  async attendreConfirmation(paiementId, onStatutChange) {
    const MAX_ATTENTE = 180; // 3 minutes
    const INTERVALLE = 5;    // 5 secondes
    let tentatives = 0;

    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        tentatives++;
        try {
          const data = await this.verifier(paiementId);
          onStatutChange(data.statut);

          if (data.statut === 'confirme') {
            clearInterval(interval);
            resolve(data);
          } else if (data.statut === 'echoue' || data.statut === 'annule') {
            clearInterval(interval);
            reject(new Error('Paiement échoué ou annulé'));
          } else if (tentatives * INTERVALLE >= MAX_ATTENTE) {
            clearInterval(interval);
            reject(new Error('Délai d\'attente dépassé. Vérifiez votre téléphone.'));
          }
        } catch (e) {
          clearInterval(interval);
          reject(e);
        }
      }, INTERVALLE * 1000);
    });
  },
};


// ============================================================
// CLIENTS (CRM — admin)
// ============================================================

export const clientsService = {
  getAll(filtres = {}) {
    return api.get('/clients/', filtres);
  },

  getDetail(id) {
    return api.get(`/clients/${id}/`);
  },

  getResume(id) {
    return api.get(`/clients/${id}/resume/`);
  },

  modifier(id, data) {
    return api.patch(`/clients/${id}/`, data);
  },

  getAdresses(clientId) {
    return api.get(`/clients/${clientId}/adresses/`);
  },

  ajouterAdresse(clientId, adresse) {
    return api.post(`/clients/${clientId}/adresses/`, adresse);
  },

  getHistoriqueFidelite(clientId) {
    return api.get(`/clients/${clientId}/historique_fidelite/`);
  },

  getRecommendations(clientId) {
    return api.get(`/clients/${clientId}/recommandations/`);
  },
};


// ============================================================
// UTILISATEURS (ADMIN)
// ============================================================

export const userService = {
  block(userId) {
    return api.post(`/dashboard/users/${userId}/block/`);
  },
  delete(userId) {
    return api.delete(`/dashboard/users/${userId}/`);
  },
  createVendor(vendeur) {
    return api.post('/auth/creer-vendeur/', vendeur);
  },
};


// ============================================================
// DASHBOARD (admin)
// ============================================================

export const dashboardService = {
  getStats() {
    return api.get('/dashboard/stats/');
  },
};
